/**
 * Bridge between the mock services and the live endeeko Firebase Functions backend
 * (firebase-functions/functions/src/apps/endeeko — exported as the `endeeko` codebase's
 * `endeekoApi.<name>` callables).
 *
 * Behaviour is controlled by env so the app is runnable with zero backend:
 *   VITE_USE_BACKEND=true       → call the real httpsCallable
 *   (unset / false)             → use the provided mock (default for the demo)
 *   VITE_ENDEEKO_FN_PREFIX      → deployed callable name prefix (default endeeko-)
 *
 * Every service stays mock-first via `withBackend(...)`: when the backend is disabled
 * OR a live call fails (dev or prod), the local mock is used so the UI never breaks
 * and callers never see an unhandled rejection.
 */
import { httpsCallable } from 'firebase/functions';
import { getFns } from './firebase';

export const backendEnabled = import.meta.env.VITE_USE_BACKEND === 'true';

// Backend functions deploy under the wire name `endeeko-<exportName>` (see
// firebase-functions/.../apps/endeeko/index.ts), so the callable prefix is
// `endeeko-` — NOT `endeekoApi-`.
const PREFIX = import.meta.env.VITE_ENDEEKO_FN_PREFIX ?? 'endeeko-';

/** Invoke an endeeko callable by its logical name (e.g. "getCompanyDashboard"). */
export async function callable<TRes, TReq = unknown>(name: string, payload?: TReq): Promise<TRes> {
  const fn = httpsCallable<TReq, TRes>(getFns(), `${PREFIX}${name}`);
  const res = await fn(stripUndefined(payload) as TReq);
  return res.data;
}

/** The callable SDK encodes `undefined` as `null`, which optional zod fields reject (400). Drop them. */
function stripUndefined(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stripUndefined);
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== undefined).map(([k, x]) => [k, stripUndefined(x)]),
    );
  }
  return v;
}

/**
 * PER-CALLABLE circuit breaker. A failing callable only short-circuits ITSELF —
 * never the whole backend — so one missing/rejected function (e.g. a 404 on an
 * unimplemented callable, or a permission-denied on a company the user isn't a
 * member of) can't disable every other live call. A later success clears it.
 */
const COOLOFF_MS = 120_000;
const downUntil = new Map<string, number>();
const warned = new Set<string>();

/** Firebase callable error code without the `functions/` prefix (e.g. "permission-denied"). */
function errorCode(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  return code.startsWith('functions/') ? code.slice('functions/'.length) : code;
}

/**
 * Per-request errors specific to THIS call/args — fall back to mock but do NOT
 * cool off, since the next call may carry a valid company/auth/argument. Only
 * transient/infra failures cool the callable off for a window.
 */
const PER_REQUEST = new Set([
  'permission-denied', 'unauthenticated', 'invalid-argument', 'not-found', 'failed-precondition', 'already-exists',
]);

/**
 * Try the live backend when enabled, otherwise run the mock. A failed live call
 * (dev OR prod) falls back to the mock so the UI never breaks and callers never
 * see an unhandled rejection. Browser-level CORS/4xx logs for the failed request
 * can't be suppressed from JS; our own warning is deduped once per callable.
 */
export async function withBackend<T>(name: string, payload: unknown, mock: () => Promise<T>): Promise<T> {
  if (!backendEnabled) return mock();
  if (Date.now() < (downUntil.get(name) ?? 0)) return mock(); // this callable is cooling off
  try {
    const data = await callable<T>(name, payload);
    downUntil.delete(name);
    warned.delete(name);
    return data;
  } catch (err) {
    // Cool off only on transient/infra failures — never on per-request errors.
    if (!PER_REQUEST.has(errorCode(err))) downUntil.set(name, Date.now() + COOLOFF_MS);
    if (!warned.has(name)) {
      warned.add(name);
      console.warn(`[endeeko] "${name}" live call failed — serving mock for this call.`, err);
    }
    return mock();
  }
}
